import React from 'react';
import {
  alpha,
  Box,
  Button,
  FormControl,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import BackButton from '../backButton';
import Label from '../Label';
import ModalDelete from '../ModalDelete';

export default function MorePromoCodes({
  deleteModal,
  handleCloseMorePromoCodes,
  promoCode,
  handleDelete,
  handleOpenDeleteModal,
  handleCloseDeleteModal,
  handleOpenPagePromoCodes,
  role,
  operatorsMap,
}) {
  const theme = useTheme();

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const editButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
    },
  };

  const removeButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.error.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.error.main, 0.85),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
    },
  };

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  };

  if (!promoCode) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <BackButton back={handleCloseMorePromoCodes} type="promoCode" />
        <Box sx={{
          mt: 1, pb: 5, display: 'flex', flexDirection: 'column', alignItems: 'left',
        }}
        >
          <Typography variant="h3">Promo Codes</Typography>
          <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>View all details of the promo code</Typography>
        </Box>
        <Box sx={{ padding: 2, borderTop: '1px solid', borderColor: theme.palette.grey[300] }}>
          <Box sx={{ width: '70%', mt: 2 }}>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Code Name</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.code ? promoCode.code : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Description</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.description ? promoCode.description : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Type</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.type ? promoCode.type : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Status</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.status ? promoCode.status : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Validity Period (Days)</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.validityPeriodDays ? `${promoCode.validityPeriodDays} days` : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Application Limit</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.applicationLimit ? promoCode.applicationLimit : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Discount Percentage</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={promoCode.discountPercentage !== undefined ? `${promoCode.discountPercentage}%` : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Applicable Zones</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={Array.isArray(promoCode.applicableZones) && promoCode.applicableZones.length > 0 ? promoCode.applicableZones.join(', ') : 'None'} />
            </FormControl>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Assigned Operators</Typography>
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {Array.isArray(promoCode?.assignedOperators)
                    && promoCode.assignedOperators.length > 0 ? (
                      promoCode.assignedOperators.map((opId) => (
                        <Label
                          key={opId}
                          variant="soft"
                          color="primary"
                        >
                          {operatorsMap?.[opId] || 'Unknown'}
                        </Label>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: theme.palette.grey[500] }}>
                        No Operators Assigned
                      </Typography>
                    )}
                </Stack>
              </Box>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{
          display: 'flex', justifyContent: 'right', borderTop: '1px solid', borderColor: theme.palette.grey[300], pt: 3,
        }}
        >
          <Button sx={buttonStyle} onClick={() => handleCloseMorePromoCodes()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          {(role === 900 || role === 101 || role === 111 || role === 102) && (
            <>
              <Button sx={removeButtonStyle} onClick={() => handleOpenDeleteModal()}>
                <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                  Remove Promo Code
                </Typography>
              </Button>
              <Button sx={editButtonStyle} onClick={() => handleOpenPagePromoCodes(promoCode)}>
                <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                  Edit
                </Typography>
              </Button>
            </>
          )}
        </Box>
      </Box>
      {deleteModal && (
        <ModalDelete
          deleteModal={deleteModal}
          handleDelete={handleDelete}
          handleClose={handleCloseDeleteModal}
          data={promoCode}
        />
      )}
    </>
  );
}
